import type { AccommodationDetail } from '@/api/accommodation/types'
import { unitFullName, unitPhotos } from '@/lib/unit'
import {
  SITE_NAME,
  coverImageUrl,
  getSiteOrigin,
  jsonLdScript,
  markdownAlternate,
  noindexMeta,
  type HeadResult,
  type MetaTag,
} from './siteMeta'

const MAX_JSONLD_IMAGES = 6
const MAX_DESCRIPTION_LENGTH = 200

function sortedPhotoUrls(location: AccommodationDetail): string[] {
  return [...unitPhotos(location.unit, location)]
    .sort((a, b) => b.priority - a.priority)
    .map((photo) => photo.url.original)
}

function detailUrl(location: AccommodationDetail, origin: string): string {
  return `${origin}/ubytovani/${location.slug}/${location.unit.slug}`
}

// Units of one place share the address and description, so the unit name and
// capacity are what keep their titles and descriptions apart.
function buildTitle(location: AccommodationDetail): string {
  return `${unitFullName(location, location.unit)} – ${location.address.city}, ${location.region.name}`
}

// Share previews (messengers, social networks) pitch the offer instead of the
// address; the SEO <title> stays untouched.
function buildShareTitle(location: AccommodationDetail): string {
  return `Last-minute nabídka ${location.name} - ${location.region.name}`
}

function buildDescription(location: AccommodationDetail): string {
  const { facility, conditions, unit } = location
  const features: string[] = []

  if (facility.pool) features.push('bazén')
  if (facility.whirlpool || facility.hotTub) features.push('vířivka')
  if (facility.sauna) features.push('sauna')
  if (unit.facility.general.indoorFireplace || unit.facility.general.woodHeating) {
    features.push('krb')
  }
  if (conditions.pets) features.push('mazlíčci vítáni')

  const featurePart = features.length > 0 ? `, ${features.slice(0, 4).join(', ')}` : ''
  const beds = unit.totals.beds
  const personsLabel = beds === 1 ? 'osoba' : beds >= 2 && beds <= 4 ? 'osoby' : 'osob'
  // "Celý objekt" says nothing new for a cottage; the type only tells the
  // units of a guesthouse apart.
  const typePart = unit.siblings.length > 0 ? `, ${unit.type.name.toLowerCase()}` : ''
  const description = `${unitFullName(location, unit)} – ubytování ${location.address.city}, ${location.region.name}. ${location.category.name}${typePart}, ${beds} ${personsLabel}${featurePart}.`

  if (description.length <= MAX_DESCRIPTION_LENGTH) return description
  return `${description.slice(0, MAX_DESCRIPTION_LENGTH - 1).trimEnd()}…`
}

/** Czech amenity labels for the facility flags exposed on the meta subset. */
function buildAmenityFeatures(location: AccommodationDetail): Array<Record<string, unknown>> {
  const { facility, unit } = location
  const amenities: string[] = []

  if (facility.pool) amenities.push('Bazén')
  if (facility.whirlpool || facility.hotTub) {
    amenities.push('Vířivka')
  }
  if (facility.sauna) amenities.push('Sauna')
  if (unit.facility.general.indoorFireplace || unit.facility.general.woodHeating) {
    amenities.push('Krb')
  }

  return amenities.map((name) => ({
    '@type': 'LocationFeatureSpecification',
    name,
    value: true,
  }))
}

/** Format a per-night CZK price range, e.g. "1 575–2 500 Kč za noc". */
function formatPriceRange(min: number, max: number): string {
  const format = (price: number) => price.toLocaleString('cs-CZ')
  return min === max ? `${format(min)} Kč za noc` : `${format(min)}–${format(max)} Kč za noc`
}

/** Map bookable date windows to schema.org Offers with per-night pricing. */
function buildOffersJsonLd(location: AccommodationDetail, url: string): Array<Record<string, unknown>> {
  return location.unit.offers.map((offer) => ({
    '@type': 'Offer',
    url,
    price: offer.minDiscountPrice,
    priceCurrency: 'CZK',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: offer.minDiscountPrice,
      priceCurrency: 'CZK',
      unitText: 'noc',
    },
    availability: 'https://schema.org/InStock',
    availabilityStarts: offer.start,
    availabilityEnds: offer.end,
  }))
}

function buildLodgingJsonLd(
  location: AccommodationDetail,
  url: string,
  images: string[],
  description: string,
): Record<string, unknown> {
  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LodgingBusiness',
    '@id': `${url}#lodging`,
    name: unitFullName(location, location.unit),
    description,
    url,
    petsAllowed: location.conditions.pets,
    address: {
      '@type': 'PostalAddress',
      streetAddress: location.address.street,
      addressLocality: location.address.city,
      postalCode: location.address.zip,
      addressRegion: location.region.name,
      addressCountry: 'CZ',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: location.address.latitude,
      longitude: location.address.longitude,
    },
    containedInPlace: {
      '@type': 'Place',
      name: location.region.name,
    },
  }

  if (images.length > 0) jsonLd.image = images.slice(0, MAX_JSONLD_IMAGES)

  const amenities = buildAmenityFeatures(location)
  if (amenities.length > 0) jsonLd.amenityFeature = amenities

  const offers = buildOffersJsonLd(location, url)
  if (offers.length > 0) {
    jsonLd.makesOffer = offers
    jsonLd.priceRange = formatPriceRange(
      Math.min(...location.unit.offers.map((offer) => offer.minDiscountPrice)),
      Math.max(...location.unit.offers.map((offer) => offer.maxDiscountPrice)),
    )
  }

  return jsonLd
}

function buildBreadcrumbJsonLd(location: AccommodationDetail, url: string, origin: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Domů', item: origin },
      // Mirrors the visible DetailBreadcrumb: the region landing page is the
      // middle crumb (every /enum region has one, see landing registry).
      {
        '@type': 'ListItem',
        position: 2,
        name: location.region.name,
        item: `${origin}/pobyty/${location.region.slug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: unitFullName(location, location.unit),
        item: url,
      },
    ],
  }
}

/**
 * Build per-unit head tags (title, description, Open Graph, Twitter, canonical
 * link, `LodgingBusiness` and `BreadcrumbList` JSON-LD) for the detail route.
 * Pass `origin` explicitly in server-only code paths, otherwise the current
 * `window.location.origin` is used. A unit that can't be booked (a preview of
 * a paused or not yet approved one) is noindex without a canonical.
 */
export function buildAccommodationHead(location: AccommodationDetail, origin: string = getSiteOrigin()): HeadResult {
  const url = detailUrl(location, origin)
  const images = sortedPhotoUrls(location)
  const mainImage = images[0] ?? coverImageUrl(origin)
  // Generated og:image banner takes priority over the raw cover photo: the
  // unit's own (from its photos), else the location's, else the cover photo.
  const ogImage = location.unit.ogImage ?? location.ogImage
  const ogImageUrl = ogImage?.url ?? mainImage
  const title = buildTitle(location)
  const shareTitle = buildShareTitle(location)
  const description = buildDescription(location)

  const meta: MetaTag[] = [
    { title },
    { name: 'description', content: description },
    { property: 'og:type', content: 'place' },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:locale', content: 'cs_CZ' },
    { property: 'og:url', content: url },
    { property: 'og:title', content: shareTitle },
    { property: 'og:description', content: description },
    { property: 'og:image', content: ogImageUrl },
    {
      property: 'og:image:alt',
      content: unitFullName(location, location.unit),
    },
    ...(ogImage
      ? [
          {
            property: 'og:image:width',
            content: String(ogImage.width),
          },
          {
            property: 'og:image:height',
            content: String(ogImage.height),
          },
        ]
      : []),
    {
      property: 'place:location:latitude',
      content: String(location.address.latitude),
    },
    {
      property: 'place:location:longitude',
      content: String(location.address.longitude),
    },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:url', content: url },
    { property: 'twitter:title', content: shareTitle },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: ogImageUrl },
    ...(location.unit.sellable ? [] : noindexMeta()),
  ]

  return {
    meta,
    links: location.unit.sellable ? [{ rel: 'canonical', href: url }, markdownAlternate(`${url}.md`)] : [],
    scripts: [
      jsonLdScript(buildLodgingJsonLd(location, url, images, description)),
      jsonLdScript(buildBreadcrumbJsonLd(location, url, origin)),
    ],
  }
}
